import {
  FREE_TRIAL_BOOKING_END_DATE,
  FREE_TRIAL_BOOKING_START_DATE,
  FREE_TRIAL_TIME_WINDOW_LABEL,
} from '@mds/shared/lib/free-trial';

export interface SiteLink {
  label: string;
  to?: string;
  href?: string;
}

const PHONE_DIGITS = '96171632449';

export const CONTACT = {
  phone: `+${PHONE_DIGITS.slice(0, 3)} ${PHONE_DIGITS.slice(3, 5)} ${PHONE_DIGITS.slice(5, 8)} ${PHONE_DIGITS.slice(8)}`,
  phoneHref: `tel:+${PHONE_DIGITS}`,
  whatsappHref: `https://wa.me/${PHONE_DIGITS}`,
  email: 'admin@allinonedigital.com.au',
  place: 'Minieh, North Lebanon',

  town: 'Minieh',
  address: {
    en: 'Minieh, Main Road, Hay Hamdoun Junction',
    ar: 'المنية، الطريق العام، مفرق حي حمدون',
  },
  addressLines: [
    'Minieh Digital Square',
    'Main Road, Hay Hamdoun Junction',
    'Minieh, North Lebanon',
  ],
  mapEmbedSrc:
    'https://www.google.com/maps?q=Minieh+Digital+Square,+Minieh,+North+Lebanon&output=embed',
  hours: 'Mon–Sat, 8 AM–5 PM',
  openTimes: '8 AM–5 PM',
  openDays: 'Monday to Saturday. Members have 24/7 access.',
} as const;

export const OPENING_HOURS: { days: string; hours: string }[] = [
  { days: 'Monday – Friday', hours: '8:00 AM – 5:00 PM' },
  { days: 'Saturday', hours: '8:00 AM – 5:00 PM' },
  { days: 'Sunday', hours: 'Closed (events only)' },
];

export const NAV: Required<Pick<SiteLink, 'label' | 'to'>>[] = [
  { label: 'Spaces', to: '/spaces' },
  { label: 'Events', to: '/events' },
  { label: 'About', to: '/about' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact', to: '/contact' },
];

export const FOOTER_COLUMNS: { heading: string; items: SiteLink[] }[] = [
  {
    heading: 'Explore',
    items: [
      { label: 'Home', to: '/' },
      { label: 'All spaces', to: '/spaces' },
      { label: 'Book a desk', to: '/book/daily-desk' },
      { label: 'Events & workshops', to: '/events' },
      { label: 'About MDS', to: '/about' },
      { label: 'Careers', to: '/careers' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    heading: 'Get in touch',
    items: [
      { label: CONTACT.phone, href: CONTACT.phoneHref },
      { label: CONTACT.email, href: `mailto:${CONTACT.email}` },
      { label: CONTACT.place },
      { label: CONTACT.hours },
    ],
  },
];

export const SOCIAL: {
  id: 'instagram' | 'facebook' | 'linkedin' | 'whatsapp';
  label: string;
  href: string | null;
}[] = [
  { id: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/miniehdigitalsquare/' },
  {
    id: 'facebook',
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61593866396633',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/minieh-digital-square/',
  },
  { id: 'whatsapp', label: 'WhatsApp', href: CONTACT.whatsappHref },
];

export const FAQ: { question: string; answer: string }[] = [
  {
    question: 'Do I need to book a desk in advance?',
    answer:
      'Not for a daily desk. Walk in and pay at the front desk. Monthly and six-month holders keep the same desk, so those are arranged when you sign up. Rooms, the hall and the studio are always booked ahead.',
  },
  {
    question: 'How do I pay?',
    answer:
      'In person at the front desk, in cash or by card. Hall hire takes a deposit to hold the date; the balance is due on the day. We invoice companies on request.',
  },
  {
    question: 'Is there a student rate?',
    answer:
      'Yes. The Student Evening Desk covers 6 PM to midnight for a flat monthly rate. Bring an enrolment letter or a student card when you sign up.',
  },
  {
    question: 'Can I get in outside opening hours?',
    answer:
      'Members have 24/7 access. Everyone else is welcome between 8 AM and 5 PM, Monday to Saturday.',
  },
  {
    question: 'What comes with the café hall?',
    answer:
      'The PA, two microphones, the projector and the counter. Four layouts are priced; anything else we quote. Catering is per head and extra chairs are per event.',
  },
  {
    question: 'Can I record in the studio without knowing the equipment?',
    answer:
      'Yes. Self-operated time is available once you have been shown the desk, which takes about twenty minutes. If you would rather not touch it, add an operator and you leave with the files.',
  },
  {
    question: 'Is parking available?',
    answer: 'Free on-site parking, for desk holders, room bookings and event attendees alike.',
  },
];

export const ABOUT = {
  hero: {
    lines: ['Build local', 'Grow global'],
    description:
      'World-class opportunities can begin locally. MDS develops local talent, businesses and connections, and builds the pathways that take them global.',
    cta: { label: 'See the spaces', to: '/spaces' },
  },
  mission:
    'We empower ambitious individuals, startups, investors and strategic partners to connect, learn and grow, right here in Minieh.',
  vision:
    'To transform Minieh into a thriving innovation hub where local talent, global opportunities and community investment come together.',
  questions: [
    {
      word: 'Do',
      question: 'What do we do?',
      answer:
        'Collaborative workspaces, learning programmes, networking opportunities and strategic partnerships.',
    },
    {
      word: 'Value',
      question: 'How do we create value?',
      answer:
        'By combining workspace, community, learning and strategic partnerships into one connected ecosystem.',
    },
    {
      word: 'Who',
      question: 'Who do we serve?',
      answer: 'Students, freelancers, startups, investors and partners.',
    },
    {
      word: 'Why',
      question: 'Why do we exist?',
      answer: 'To help ambitious people connect, grow and unlock meaningful opportunities.',
    },
  ],
  problem: {
    title: 'Talent has never been',
    highlight: 'the problem.',
    lead: 'North Lebanon is full of ambitious people, capable professionals and meaningful ideas. What has been missing is the environment that helps them grow together.',
    points: [
      'MDS exists to connect people with opportunities, ideas with action, and local ambition with global possibilities.',
      "No one should have to leave their community to build a meaningful future. With the right knowledge, partnerships and support, people don't just build careers. They build stronger communities.",
    ],
  },
  coreValues: [
    {
      title: 'Community',
      tagline: 'We grow together.',
      body: 'Meaningful relationships build stronger people, stronger businesses and a stronger community.',
    },
    {
      title: 'Ambition',
      tagline: 'We think beyond limits.',
      body: 'World-class careers and businesses can be built right here in Minieh.',
    },
    {
      title: 'Collaboration',
      tagline: 'We create opportunity together.',
      body: 'The best ideas, partnerships and opportunities emerge when people work together.',
    },
    {
      title: 'Impact',
      tagline: 'We build what truly matters.',
      body: 'We measure ourselves by real impact on individuals, businesses and our community, not by activity alone.',
    },
  ],
  audiences: [
    {
      name: 'Local Talent',
      who: 'Students, graduates, freelancers & early-stage founders',
      lookingFor: 'Skills, workspace, community and pathways to opportunity.',
    },
    {
      name: 'Diaspora Investors',
      who: 'Lebanese diaspora, investors & business owners',
      lookingFor: 'Credible local opportunities, insight and trusted connections.',
    },
    {
      name: 'Global Employers & Partners',
      who: 'International businesses and strategic partners',
      lookingFor: 'Reliable talent, local presence and accountable partnerships.',
    },
  ],
  square: {
    title: 'Inside Minieh Digital Square',
    tagline: 'One ecosystem · every kind of space',
  },
} as const;

/**
 * The opening week's free trial, shown on the home page until `endsAt`.
 *
 * `endsAt` carries Beirut's offset for that evening: summer time only ends at
 * midnight going into Sunday 25 October, so 6 PM on the 24th is still +03:00.
 */
export const TRIAL_WEEK = {
  badge: 'Opening week offer',
  title: 'MDS Free Trial Week',
  description: 'Try the coworking floor for free, 19–24 October. Come for one day or all six.',
  days: { from: FREE_TRIAL_BOOKING_START_DATE, to: FREE_TRIAL_BOOKING_END_DATE },
  facts: [{ title: FREE_TRIAL_TIME_WINDOW_LABEL, note: 'Daily, last entry 5:00 PM' }],
  cta: { label: 'Book Your Free Desk', to: '/book/daily-desk?offer=trial' },
  endsAt: `${FREE_TRIAL_BOOKING_END_DATE}T18:00:00+03:00`,
} as const;

/**
 * The careers page: its headline and the words it rotates through. The form's
 * roles are the API's `WORK_FIELDS`, not copy.
 */
export const CAREERS = {
  title: 'Join the crew at MDS.',
  lookingFor: "We're always looking for",
  /** Cycled through after `lookingFor`, one at a time. */
  words: [
    'curious minds.',
    'quick learners.',
    'problem solvers.',
    'warm hosts.',
    'creative thinkers.',
  ],
} as const;

export const SITE = {
  name: 'MDS',
  fullName: 'Minieh Digital Square',
  tagline: 'Desks, rooms and a hall in the middle of Minieh.',
} as const;
